import React from 'react';
import { useAppSelector } from 'tradera/state/hooks';
import { logger } from 'packages/logger';
import { useIsFeatureEnabled } from 'tradera/hooks/use-is-feature-enabled';
import ApplicationErrorBoundary from 'tradera/components/application-error-boundary/application-error-boundary';
import { LastChance } from './last-chance/last-chance';
import InstagramFeed from './instagram-feed/instagram-feed';
import { PopularCategories } from './popular-categories/popular-categories';
import { RecentlyViewed } from './recently-viewed/recently-viewed';
import { PicksForYou } from './picks-for-you/picks-for-you';
import { CategoryLinks } from './category-links/category-links';
import type { CategoryLinksData } from './category-links/category-links';
import WishList from './wish-list/wish-list';
import {
    LAST_CHANCE,
    PICKS_FOR_YOU,
    POPULAR_CATEGORIES,
    RECENTLY_VIEWED,
    INSTAGRAM_FEED,
    WISH_LIST,
    FAVOURITE_SELLERS,
    CATEGORY_LINKS,
    SAVED_SEARCHES,
    LAST_SEARCH,
    DISCOVER_SUGGESTIONS,
    FAVOURITE_SELLERS_ITEMS,
    NEAR_YOU
} from './content-types';
import { SavedSearches } from './saved-searches/saved-searches';
import { ConditionalRenderSection } from './conditional-render-section';
import { LastSearch } from './last-search/last-search';
import { Sellers } from './sellers/sellers';
import { SellerItems } from './sellers/seller-items';
import { DiscoverSuggestions } from './personalised/discover-suggestions';
import type {
    StartPageConfig,
    StartPageContent,
    StartpageContentDataHiddenOn
} from 'tradera/state/views/start-page/types';
import { isNextJs } from 'tradera/utils/nextjs';
import WishListRtkq from './wish-list/wish-list-rtkq';
import { RandomItemList } from './item-list/random-item-list';

type ContainerProps = {
    container: StartPageContent;
    inSPA: boolean;
    videoEnabled: boolean;
};
const Container = ({ container, inSPA, videoEnabled }: ContainerProps) => {
    const { type, data } = container;

    switch (type) {
        case LAST_CHANCE:
            return <LastChance />;
        case RECENTLY_VIEWED:
            return <RecentlyViewed />;
        case POPULAR_CATEGORIES:
            return (
                <PopularCategories
                    containerType={POPULAR_CATEGORIES}
                    data={data}
                    inSPA={inSPA}
                    videoEnabled={videoEnabled}
                />
            );
        case PICKS_FOR_YOU:
            return <PicksForYou />;
        case INSTAGRAM_FEED:
            return <InstagramFeed />;
        case WISH_LIST:
            return isNextJs ? <WishListRtkq /> : <WishList />;
        case FAVOURITE_SELLERS:
            return (
                <>
                    <ConditionalRenderSection
                        onlyForLoggedOutUsers
                        featureToggle="start-page-item-lists">
                        <RandomItemList />
                        <RandomItemList />
                        <RandomItemList />
                        <RandomItemList />
                        <RandomItemList />
                    </ConditionalRenderSection>
                    <Sellers />
                    <ConditionalRenderSection onlyForLoggedInUsers>
                        <SellerItems />
                    </ConditionalRenderSection>
                </>
            );
        case SAVED_SEARCHES:
            return (
                <ConditionalRenderSection
                    onlyForLoggedInUsers
                    featureToggle="saved-searches-landing">
                    <SavedSearches />
                </ConditionalRenderSection>
            );
        case LAST_SEARCH:
            return (
                <ConditionalRenderSection featureToggle="last-search-landing">
                    <LastSearch />
                </ConditionalRenderSection>
            );
        case CATEGORY_LINKS:
            return (
                <ConditionalRenderSection featureToggle="category-links">
                    <CategoryLinks data={data as CategoryLinksData} />
                </ConditionalRenderSection>
            );
        case DISCOVER_SUGGESTIONS:
            return (
                <ConditionalRenderSection featureToggle="discover-suggestions">
                    <DiscoverSuggestions />
                </ConditionalRenderSection>
            );
        case FAVOURITE_SELLERS_ITEMS:
            // Only implemented from apps
            return null;
        case NEAR_YOU:
            // Only implemented from apps
            return null;
        default:
            if (
                !(data as StartpageContentDataHiddenOn).hiddenOn.includes('Web')
            ) {
                logger(`Unknown content type ${type}`);
            }
            return null;
    }
};

const PageContent = () => {
    const { content } = useAppSelector(
        (state) => state.views.startPage.config as StartPageConfig
    );
    const videoEnabled = useIsFeatureEnabled('start-page-contentful-video');
    if (!content) {
        logger('Start page content missing');
        return (
            <h3 className="d-flex justify-content-center py-5 text-warning">
                Start page content missing!
            </h3>
        );
    }
    return (
        <>
            {content.map((container, index) => (
                <ApplicationErrorBoundary key={container.type + index}>
                    <Container
                        container={container}
                        inSPA={true}
                        videoEnabled={videoEnabled}
                    />
                </ApplicationErrorBoundary>
            ))}
        </>
    );
};

export default PageContent;
